"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import ButtonClose from "@/shared/ButtonClose";
import Logo from "@/shared/Logo";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_DEMO } from "@/data/navigation";
import SocialsList from "@/shared/SocialsList";
import LangDropdown from "@/app/[lang]/[currency]/(client-components)/(Header)/LangDropdown";
import { useTranslation } from "react-i18next";
import { useParams, useRouter } from "next/navigation";
import type { Route } from "next";
import { useAppSelector } from "@/app/lib/hooks";
import { useMenu } from "@/shared/Menus/Menu";
import { usePartnersMenu } from "@/shared/Menus/Partners";
import { useMoreMenu } from "@/shared/Menus/More";
import { useGiftMenu } from "@/shared/Menus/Gift";
import { useAppDispatch } from "@/app/lib/hooks";
import { getContinents } from "@/app/lib/reducers/continentReducer";
import { getCountries } from "@/app/lib/reducers/countriesReducer";
import { clearActingBackups } from "@/app/lib/clearActingBackups";

// Admin "Prise de contrôle" modal — lazy + SSR-off (role-1 only, rarely
// rendered). Same component the desktop AvatarDropdown opens.
const ImpersonationModal = dynamic(
  () => import("@/components/admin/ImpersonationModal"),
  { ssr: false, loading: () => null }
);

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

// Continent ISO → emoji. EU intentionally maps to a globe (🌐) rather than
// 🇪🇺 since the regional-indicator pair doesn't render as a flag on Windows
// — matches the choice already made in MobileLanding for consistency.
const CONTINENT_EMOJI: Record<string, string> = {
  EU: "🌐",
  AS: "🌏",
  NA: "🌎",
  SA: "🌎",
  AF: "🌍",
  OC: "🏝️",
  ME: "🕌",
  AN: "❄️",
};

const titleCase = (s?: string): string => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-"),
    )
    .join(" ");
};

// Emoji mapping — keyed by the menu hooks' `href` so a row's icon stays in
// sync with whatever the desktop megamenu sources surface. Anything missing
// falls back to the section's parent emoji.
const SERVICE_EMOJI: Record<string, string> = {
  "/airport-pickup": "✈️",
  "/transfer": "🚐",
  "/business-trip": "💼",
  "/cruise-tour": "⛵",
};
const WORK_EMOJI: Record<string, string> = {
  "/become-a-guide": "🧭",
  "/becoming-guide": "👔",
  "/travel-agents": "🏢",
  "/recruitment": "👔",
};
const MORE_EMOJI: Record<string, string> = {
  "/guide-missions": "👷",
  "/gift-cards": "🎁",
  "/blog": "📰",
  "/faq": "❓",
  "/about-us": "ℹ️",
  "/contact": "✉️",
};

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO,
  onClickClose,
}) => {
  const { t } = useTranslation("guydeez");
  const router = useRouter();
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const user = useAppSelector((state) => state.auth.user);

  // 'root' | 'destinations' | 'services' | 'work' | 'more' | 'gift'. The
  // Destinations branch adds a 3rd level (continent → countries) tracked by
  // `selectedContinent` — null shows the continent list, a code shows that
  // continent's countries.
  const [section, setSection] = useState<
    "root" | "destinations" | "services" | "work" | "more" | "gift"
  >("root");
  const [selectedContinent, setSelectedContinent] = useState<string | null>(
    null,
  );
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  // Role 1 = super admin. Cover both shapes the backend emits the role
  // under (top-level number + nested .id) — same check as AvatarDropdown.
  const isSuperAdmin =
    user?.role === 1 || user?.roleDetails?.id === 1;

  // Same rule as AvatarDropdown: role 1 always; role 2/4 only with an active
  // impersonation grant (asked to the backend). Drives the menu item below.
  const roleId = (user?.role ?? user?.roleDetails?.id) as number | undefined;
  const [canImpersonate, setCanImpersonate] = useState(false);
  useEffect(() => {
    if (isSuperAdmin) {
      setCanImpersonate(true);
      return;
    }
    if (roleId !== 2 && roleId !== 4) {
      setCanImpersonate(false);
      return;
    }
    let cancelled = false;
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("access_token")
        : null;
    if (!token) {
      setCanImpersonate(false);
      return;
    }
    fetch(
      `${process.env.NEXT_PUBLIC_SOCKET}/api/admin/impersonate/eligibility`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => (r.ok ? r.json() : { canImpersonate: false }))
      .then((d) => {
        if (!cancelled) setCanImpersonate(Boolean(d?.canImpersonate));
      })
      .catch(() => {
        if (!cancelled) setCanImpersonate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, roleId]);

  const servicesMenu = useMenu();
  const partnersMenu = usePartnersMenu();
  const moreMenu = useMoreMenu();
  const giftMenu = useGiftMenu();
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector(
    (s: any) => s?.language?.selectedLanguage,
  ) || "fr_FR";
  const continents: any[] = useAppSelector(
    (s: any) => s?.continent?.continents,
  ) || [];
  const countries: any[] = useAppSelector(
    (s: any) => s?.countries?.countries,
  ) || [];

  // Lazy-fetch destinations data the first time the user lands on this sub
  // (same pattern as the homepage TrustStrip + desktop SectionExploreGlobe).
  useEffect(() => {
    if (section !== "destinations") return;
    if (!continents.length) (dispatch as any)(getContinents(true, selectedLanguage));
    if (!countries.length) (dispatch as any)(getCountries(null, true, selectedLanguage));
  }, [section, continents.length, countries.length, dispatch, selectedLanguage]);

  // Locale-prefixed nav helper — keeps URLs consistent with the rest of the
  // mobile flow which always carries /{lang}/{currency}/… (the middleware
  // redirects bare paths but adds a 30x roundtrip).
  const langPrefix =
    lang && currency ? `/${lang}/${currency}` : "";
  const go = (path: string) => {
    onClickClose?.();
    router.push(`${langPrefix}${path}` as Route);
  };

  // User display bits — auth slice is typed as `any`, so we read defensively.
  const firstName: string =
    user?.firstName || user?.user?.firstName || "";
  const lastName: string =
    user?.lastName || user?.user?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const initial = (firstName || user?.email || "?").charAt(0).toUpperCase();
  const createdYear = (() => {
    const raw = user?.createdAt || user?.user?.createdAt;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getFullYear()) ? null : d.getFullYear();
  })();

  const handleLogout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      clearActingBackups();
    }
    onClickClose?.();
    router.push("/login" as Route);
  };

  // Back-arrow + section title rendered above a sub-section list. Mirrors
  // the canvas design's `SubHeader` in 20-mobile-shared.jsx.
  const SubHeader: React.FC<{ title: string; onBack: () => void }> = ({
    title,
    onBack,
  }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 16px 10px",
        borderBottom: "1px solid var(--g-line)",
        background: "var(--g-cream)",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t("nav_back", { defaultValue: "Retour" })}
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "white",
          border: "1px solid var(--g-line)",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--g-burgundy)"
          strokeWidth="2.4"
          aria-hidden
        >
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </button>
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: "var(--g-burgundy)",
        }}
      >
        {title}
      </div>
    </div>
  );

  // Single source for every row in the drawer — same shape, animated
  // fade-up via the inline keyframe at the bottom of the component.
  const Row: React.FC<{
    label: string;
    sub?: string;
    emoji?: string;
    // When provided, renders the country flag PNG at /images/flags/{iso}.png
    // instead of the text/emoji slot. Used by the country list in the
    // Destinations sub-section so users see "🇮🇹" rather than "IT".
    flagIso?: string;
    badge?: string | number;
    chevron?: boolean;
    onClick: () => void;
    delay?: number;
  }> = ({
    label,
    sub,
    emoji,
    flagIso,
    badge,
    chevron = true,
    onClick,
    delay = 0,
  }) => (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "14px 16px",
        background: "transparent",
        border: "none",
        borderBottom: "1px solid var(--g-line)",
        textAlign: "left",
        cursor: "pointer",
        animation: `gd-nav-fade-up .28s ${delay}ms both`,
      }}
    >
      {flagIso ? (
        <img
          src={`/images/flags/${flagIso.toLowerCase()}.png`}
          alt=""
          width={22}
          height={16}
          style={{
            display: "block",
            objectFit: "cover",
            borderRadius: 2,
            flexShrink: 0,
          }}
          aria-hidden
        />
      ) : (
        <span
          style={{ fontSize: 18, width: 22, textAlign: "center" }}
          aria-hidden
        >
          {emoji}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "var(--g-burgundy)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: "var(--g-ink-500)", marginTop: 2 }}>
            {sub}
          </div>
        )}
      </div>
      {badge != null && (
        <span
          style={{
            fontSize: 10,
            padding: "2px 8px",
            borderRadius: 999,
            background: "var(--g-coral)",
            color: "white",
            fontWeight: 700,
          }}
        >
          {badge}
        </span>
      )}
      {chevron && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--g-ink-500)"
          strokeWidth="2"
          aria-hidden
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      )}
    </button>
  );

  return (
    <div className="flex flex-col h-[100dvh] bg-white dark:bg-neutral-900 relative">
      <style>{`
        @keyframes gd-nav-fade-up {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header — unchanged (green logo bar + close X) */}
      <div className="bg-primary-500 py-6 px-5 flex justify-between items-center z-10">
        <Logo />
        <ButtonClose onClick={onClickClose} />
      </div>

      {/* Scrollable content */}
      <div
        className="flex-grow overflow-y-auto"
        style={{ paddingBottom: 120, background: "white" }}
      >
        {/* Account header — burgundy band, only on the root view + when
            logged in. Inside sub-sections the SubHeader takes its place. */}
        {section === "root" && user && (
          <div
            style={{
              padding: "18px 18px 16px",
              background: "var(--g-burgundy)",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(255,255,255,.25)",
                color: "white",
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
                fontSize: 16,
                flexShrink: 0,
              }}
              aria-hidden
            >
              {initial}
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fullName || user?.email || t("menu_account")}
              </div>
              {createdYear && (
                <div style={{ fontSize: 11, opacity: 0.75 }}>
                  {t("nav_member_since", {
                    year: createdYear,
                    defaultValue: `Membre depuis ${createdYear}`,
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ROOT — top-level entries that mirror the desktop megamenus.
            Section entries (Services, Work, Cadeau, Plus) expand to a
            sub-view; "leaf" entries (Destinations, Contactez-nous) navigate
            directly. */}
        {section === "root" && (
          <>
            <Row
              label={t("nav_destinations", { defaultValue: "Destinations" })}
              sub={t("nav_destinations_sub", {
                defaultValue: "Pays · Villes · Continents",
              })}
              emoji="🌍"
              onClick={() => {
                setSelectedContinent(null);
                setSection("destinations");
              }}
              delay={60}
            />
            <Row
              label={t("nav_services", { defaultValue: "Nos services" })}
              sub={t("nav_services_sub", {
                defaultValue: "Transferts, tours privés, devis…",
              })}
              emoji="🛎️"
              onClick={() => setSection("services")}
              delay={100}
            />
            <Row
              label={t("nav_work_with_us", {
                defaultValue: "Travaillez avec nous",
              })}
              sub={t("nav_work_with_us_sub", {
                defaultValue: "Guides · Agences",
              })}
              emoji="🤝"
              onClick={() => setSection("work")}
              delay={140}
            />
            <Row
              label={t("contact", { defaultValue: "Contactez-nous" })}
              emoji="✉️"
              chevron={false}
              onClick={() => go("/contact")}
              delay={180}
            />
            <Row
              label={t("gift", { defaultValue: "Cartes cadeaux" })}
              emoji="🎁"
              onClick={() => setSection("gift")}
              delay={200}
            />
            <Row
              label={t("more", { defaultValue: "Plus" })}
              sub={t("nav_more_sub", {
                defaultValue: "Blog, FAQ, à propos…",
              })}
              emoji="➕"
              onClick={() => setSection("more")}
              delay={220}
            />
          </>
        )}

        {/* Destinations — level 1: continent list. Counts derived from the
            countries slice filtered by `continentId`. */}
        {section === "destinations" && !selectedContinent && (
          <>
            <SubHeader
              title={t("nav_destinations", { defaultValue: "Destinations" })}
              onBack={() => setSection("root")}
            />
            {/* "All continents" shortcut — routes to /continents and surfaces
                the same content as the main marketing page. Kept on top so
                users always have a quick way to see the global overview
                without having to drill into a single continent. */}
            <Row
              label={t("nav_dest_all_continents", {
                defaultValue: "Tous les continents",
              })}
              sub={t("nav_dest_all_continents_sub", {
                defaultValue: "Vue d'ensemble du catalogue",
              })}
              emoji="🗺️"
              onClick={() => go("/continents")}
              delay={20}
            />
            {continents
              .filter((c: any) => c.activated === true)
              .map((c: any, i: number) => {
                const code = (c.code || "").toUpperCase();
                const continentCountries = countries.filter(
                  (co: any) => co.continentId === code && co.activated,
                );
                const totalTours = continentCountries.reduce(
                  (acc: number, co: any) => acc + (co.numTours || 0),
                  0,
                );
                // Prefer the joined CMS entry title (locale-filtered by the
                // backend) so continent names render in the active language
                // — falls back to `nameTranslated` then the raw English name.
                const label = titleCase(
                  c.entry?.data?.[0]?.title ||
                    c.nameTranslated ||
                    c.name,
                );
                const subParts = [];
                if (continentCountries.length > 0) {
                  subParts.push(
                    t("nav_dest_countries_count", {
                      count: continentCountries.length,
                      defaultValue: `${continentCountries.length} pays`,
                    }),
                  );
                }
                if (totalTours > 0) {
                  subParts.push(
                    t("nav_dest_tours_count", {
                      count: totalTours,
                      defaultValue: `${totalTours} tours`,
                    }),
                  );
                }
                return (
                  <Row
                    key={code || c.name}
                    label={label}
                    sub={subParts.join(" · ")}
                    emoji={CONTINENT_EMOJI[code] || "🌍"}
                    onClick={() => setSelectedContinent(code)}
                    delay={40 + i * 30}
                  />
                );
              })}
          </>
        )}

        {/* Destinations — level 2: countries of the selected continent.
            Each row uses the country's ISO code as a "flag-text" badge
            (matches the screenshot the user showed) and surfaces tour
            counts; clicking navigates to the country page. */}
        {section === "destinations" && selectedContinent && (
          <>
            <SubHeader
              title={(() => {
                const cont = continents.find(
                  (c: any) =>
                    (c.code || "").toUpperCase() === selectedContinent,
                );
                return titleCase(
                  cont?.entry?.data?.[0]?.title ||
                    cont?.nameTranslated ||
                    cont?.name ||
                    t("nav_destinations", { defaultValue: "Destinations" }),
                );
              })()}
              onBack={() => setSelectedContinent(null)}
            />
            {countries
              .filter(
                (co: any) =>
                  co.continentId === selectedContinent && co.activated,
              )
              .sort(
                (a: any, b: any) => (b.numTours || 0) - (a.numTours || 0),
              )
              .map((co: any, i: number) => (
                <Row
                  key={co.code || co.slug || co.name}
                  label={titleCase(
                    co.entry?.data?.[0]?.title ||
                      co.nameTranslated ||
                      co.name,
                  )}
                  sub={
                    co.numTours
                      ? t("nav_dest_tours_count", {
                          count: co.numTours,
                          defaultValue: `${co.numTours} tours`,
                        })
                      : undefined
                  }
                  flagIso={co.code}
                  chevron={false}
                  onClick={() =>
                    go(
                      `/country/${
                        co.slug ||
                        co.code?.toLowerCase() ||
                        (co.name || "").toLowerCase().replace(/\s+/g, "-")
                      }`,
                    )
                  }
                  delay={30 + i * 20}
                />
              ))}
          </>
        )}

        {/* Sub-section views — backed by the existing menu hooks so item
            names, descriptions and hrefs stay in sync with the desktop
            megamenus. */}
        {section === "services" && (
          <>
            <SubHeader
              title={t("nav_services", { defaultValue: "Nos services" })}
              onBack={() => setSection("root")}
            />
            {servicesMenu.map((item: any, i: number) => (
              <Row
                key={`svc-${i}`}
                label={item.name}
                sub={item.description}
                emoji={SERVICE_EMOJI[item.href] || "🛎️"}
                chevron={false}
                onClick={() => go(item.href)}
                delay={40 + i * 30}
              />
            ))}
          </>
        )}

        {section === "work" && (
          <>
            <SubHeader
              title={t("nav_work_with_us", {
                defaultValue: "Travaillez avec nous",
              })}
              onBack={() => setSection("root")}
            />
            {partnersMenu.map((item: any, i: number) => (
              <Row
                key={`work-${i}`}
                label={item.name}
                sub={item.description}
                emoji={WORK_EMOJI[item.href] || "🤝"}
                chevron={false}
                onClick={() => go(item.href)}
                delay={40 + i * 40}
              />
            ))}
          </>
        )}

        {section === "gift" && (
          <>
            <SubHeader
              title={t("gift", { defaultValue: "Cartes cadeaux" })}
              onBack={() => setSection("root")}
            />
            {giftMenu.map((item: any, i: number) => (
              <Row
                key={`gift-${i}`}
                label={item.name}
                sub={item.description}
                emoji="🎁"
                chevron={false}
                onClick={() => go(item.href)}
                delay={40 + i * 30}
              />
            ))}
          </>
        )}

        {section === "more" && (
          <>
            <SubHeader
              title={t("more", { defaultValue: "Plus" })}
              onBack={() => setSection("root")}
            />
            {moreMenu.map((item: any, i: number) => (
              <Row
                key={`more-${i}`}
                label={item.name}
                sub={item.description}
                emoji={MORE_EMOJI[item.href] || "➕"}
                chevron={false}
                onClick={() => go(item.href)}
                delay={40 + i * 25}
              />
            ))}
          </>
        )}

        {/* MON COMPTE — only on the root view + when logged in */}
        {section === "root" && user && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--g-ink-500)",
                padding: "16px 16px 8px",
              }}
            >
              {t("nav_my_account_section", { defaultValue: "Mon compte" })}
            </div>
            <Row
              label={t("menu_account", { defaultValue: "Compte" })}
              emoji="👤"
              chevron={false}
              onClick={() => go("/account")}
              delay={210}
            />
            <Row
              label={t("my_bookings", { defaultValue: "Mes réservations" })}
              emoji="📅"
              chevron={false}
              onClick={() => go("/my-bookings")}
              delay={220}
            />
            {/* Guide dashboard — surfaced only when the connected user has
                a guide/admin role. Same predicate as the legacy NavMobile
                tabs (roles 1, 2, 3 numeric + "Guide" / "Admin" /
                "Super Admin" string variants). */}
            {[1, 2, 3, "Guide", "Super Admin", "Admin"].includes(
              user?.role,
            ) && (
              <Row
                label={t("menu_guide_dashboard", {
                  defaultValue: "Tableau de bord guide",
                })}
                emoji="👷"
                chevron={false}
                onClick={() => go("/guide-missions")}
                delay={260}
              />
            )}
            {/* Admin take-over (role 1 only). Opens the modal in place —
                we deliberately do NOT close the drawer here, since the
                modal is mounted inside it and closing would unmount it.
                A successful take-over hard-reloads the page anyway. */}
            {canImpersonate && (
              <Row
                label={t("impersonation_menu_item", {
                  defaultValue: "Prise de contrôle",
                })}
                emoji="👁️"
                chevron={false}
                onClick={() => setImpersonationOpen(true)}
                delay={270}
              />
            )}
            <button
              onClick={handleLogout}
              style={{
                margin: "12px 16px 20px",
                padding: "12px 0",
                background: "transparent",
                border: "none",
                color: "var(--g-ink-500)",
                fontSize: 13,
                fontWeight: 600,
                textAlign: "left",
                cursor: "pointer",
                width: "calc(100% - 32px)",
              }}
            >
              {t("nav_logout", { defaultValue: "Se déconnecter" })}
            </button>
          </>
        )}

        {/* Anonymous CTA — root view only */}
        {section === "root" && !user && (
          <>
            <Row
              label={t("login", { defaultValue: "Connexion" })}
              emoji="👤"
              chevron={false}
              onClick={() => go("/login")}
              delay={220}
            />
            <Row
              label={t("signup", { defaultValue: "Créer un compte" })}
              emoji="✨"
              chevron={false}
              onClick={() => go("/signup")}
              delay={260}
            />
          </>
        )}
      </div>

      {/* Footer — unchanged (Suivez-nous + SocialsList + LangDropdown) */}

      {/* Fixed footer */}
      <div className="fixed bottom-0 left-0 w-full z-20 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 px-5 py-6 flex justify-between items-center gap-4">
        {/* Left: Follow us */}
        <div className="flex items-center gap-4">
          <h3 className="text-sm text-neutral-600 dark:text-neutral-400 mb-0">
            {t("follow_us")}
          </h3>
          <SocialsList itemClass="w-9 h-9 flex items-center justify-center rounded-full bg-neutral-100 text-xl dark:bg-neutral-800 dark:text-neutral-300" />
        </div>

        {/* Right: Language dropdown */}
        <div className="flex items-center gap-2 relative">
          <LangDropdown
            className="flex"
            panelClassName="z-30 w-screen max-w-[280px] mb-3 right-0 bottom-full sm:px-0"
          />
        </div>
      </div>

      {/* Admin take-over modal (role 1, or role 2/4 with an active grant) */}
      {canImpersonate && (
        <ImpersonationModal
          open={impersonationOpen}
          onClose={() => setImpersonationOpen(false)}
        />
      )}
    </div>
  );
};

export default NavMobile;
